// Enviar un reporte HTML por WhatsApp como IMAGEN (PNG).
// Renderiza el HTML en un iframe oculto, lo captura con html2canvas y lo manda
// al endpoint /whatsapp/send (Evolution API) como media tipo 'image'.
import html2canvas from 'html2canvas';
import { buildApiUrl } from '../../utils/apiUrl';
import { supabase } from '../../supabase';

function normalizePhone(phone: string) {
  return phone.replace(/\D/g, '');
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Renderiza el HTML en un iframe oculto y devuelve el PNG en base64 (sin prefijo).
async function htmlAImagenBase64(html: string, anchoPx = 900): Promise<string> {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `position:fixed;left:-99999px;top:0;width:${anchoPx}px;height:10px;border:none;background:#fff;`;
  document.body.appendChild(iframe);
  try {
    const doc = iframe.contentDocument!;
    doc.open(); doc.write(html); doc.close();
    // Esperar a que el layout/recursos se asienten.
    await new Promise((r) => setTimeout(r, 350));
    const body = doc.body;
    const canvas = await html2canvas(body, {
      backgroundColor: '#ffffff',
      scale: 1.5,               // nitidez suficiente para texto/tabla, menos peso
      width: anchoPx,
      windowWidth: anchoPx,
      useCORS: true,
    });
    // JPEG (no PNG): un reporte con fondo blanco pesa ~10x menos en JPEG, evita que
    // Evolution rechace la imagen por tamaño. Calidad alta (0.92) se ve nítido.
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    return dataUrl.split(',')[1];
  } finally {
    document.body.removeChild(iframe);
  }
}

export interface EnviarReporteOpts {
  html: string;          // HTML completo del reporte (mismo que se imprime)
  numero: string;        // teléfono destino
  caption?: string;      // texto que acompaña la imagen
  fileName?: string;     // nombre del archivo
  empresaId?: number | string;
}

export async function enviarReporteWhatsApp(opts: EnviarReporteOpts): Promise<{ ok: boolean; error?: string }> {
  const numero = normalizePhone(opts.numero);
  if (numero.length < 8) return { ok: false, error: 'Número de teléfono inválido.' };
  try {
    const media = await htmlAImagenBase64(opts.html);
    const headers = await authHeaders();
    const res = await fetch(buildApiUrl('/whatsapp/send'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...(opts.empresaId ? { 'X-Empresa-Id': String(opts.empresaId) } : {}),
      },
      body: JSON.stringify({
        number: numero,
        text: opts.caption || 'Reporte de muestreos',
        media,
        mediatype: 'image',
        mimetype: 'image/jpeg',
        fileName: (opts.fileName || 'reporte.png').replace(/\.png$/i, '.jpg'),
      }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j?.ok === false || j?.error) {
      return { ok: false, error: j?.error || j?.message || `Error HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'No se pudo generar/enviar la imagen.' };
  }
}
